import { CreateEditFormDrawer } from 'framework/ViewTypes/contentLayout';
import { ErrorMessage, SuccessMessage, useMessageService } from 'framework/ui/Message';
import type { FormFieldsData } from 'framework/form';
import type { SourcingFilterOptions, SourcingFilters, SourcingSavedFilter } from '../../../../types';
import useRecruitmentPoolSourcingService from '../../../../useRecruitmentPoolSourcingService';
import { findMatchingSavedSourcingFilter } from '../../utils/utils';
import getSavedSourcingFiltersDrawerSections from './SavedSourcingFiltersDrawerSections';
import type { SavedSourcingFiltersFormData } from './types';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  recruitmentPoolId: string;
  filterOptions: SourcingFilterOptions;
  sourcingFilters: SourcingFilters;
  savedFilters: SourcingSavedFilter[];
  reloadSavedFilters: () => Promise<SourcingSavedFilter[]>;
  onApply: (filter: SourcingSavedFilter) => void;
  onDeleted: (ids: string[]) => void;
  canDelete: boolean;
};

const SavedSourcingFiltersDrawer = ({
  isOpen,
  onClose,
  recruitmentPoolId,
  filterOptions,
  sourcingFilters,
  savedFilters,
  reloadSavedFilters,
  onApply,
  onDeleted,
  canDelete,
}: Props) => {
  const { addMessage } = useMessageService();
  const { deleteSavedFilters } = useRecruitmentPoolSourcingService();

  const getDataFunc = async (): Promise<FormFieldsData> => {
    try {
      const refreshedFilters = await reloadSavedFilters();
      const matchingFilter = findMatchingSavedSourcingFilter(sourcingFilters, refreshedFilters);

      return {
        selectedFilterId: matchingFilter?.id ?? '',
        filterIdsToDelete: [],
      };
    } catch (error) {
      addMessage(new ErrorMessage('Unable to load saved sourcing filters.'));
      throw error;
    }
  };

  return (
    <CreateEditFormDrawer
      key={isOpen ? 'saved-open' : 'saved-closed'}
      isOpen={isOpen}
      getDataFunc={isOpen ? getDataFunc : null}
      onClose={onClose}
      title="Saved filters"
      header="Apply or manage your saved sourcing filters"
      initialData={{
        selectedFilterId: findMatchingSavedSourcingFilter(sourcingFilters, savedFilters)?.id ?? '',
        filterIdsToDelete: [],
      }}
      isCreateRequest={false}
      handleDirty={false}
      submitButtonTitle="Apply"
      submitFormFunc={async (data: FormFieldsData) => {
        const selectedFilterId = typeof data.selectedFilterId === 'string' ? data.selectedFilterId : '';
        const filterIdsToDelete = Array.isArray(data.filterIdsToDelete)
          ? Array.from(
              new Set(
                data.filterIdsToDelete.filter(
                  (id): id is string => typeof id === 'string' && savedFilters.some(filter => filter.id === id),
                ),
              ),
            )
          : [];
        const formData: SavedSourcingFiltersFormData = { selectedFilterId, filterIdsToDelete };
        const selected = savedFilters.find(
          filter => filter.id === selectedFilterId && !filterIdsToDelete.includes(filter.id),
        );

        if (filterIdsToDelete.length) {
          try {
            await deleteSavedFilters(recruitmentPoolId, filterIdsToDelete);
          } catch {
            addMessage(new ErrorMessage('Unable to delete the selected sourcing filters.'));
            return {};
          }

          onDeleted(filterIdsToDelete);
          addMessage(
            new SuccessMessage(
              filterIdsToDelete.length === 1
                ? 'Sourcing filter deleted successfully.'
                : 'Sourcing filters deleted successfully.',
            ),
          );
        }

        if (selected) onApply(selected);
        onClose();
        return formData;
      }}
      getSections={() =>
        getSavedSourcingFiltersDrawerSections({
          savedFilters,
          filterOptions,
          canDelete,
        })
      }
      width={680}
    />
  );
};

export default SavedSourcingFiltersDrawer;
